import { BigDecimal } from "@solomon-labs/bigdecimal";
import type { PublicKey } from "@solomon-labs/publickey";
import { ConnectionManager } from "@solomon-labs/solana-connection";
import { SolomonStake } from "@solomon-labs/solomon-protocol";
import { create } from "zustand";

import type { StakeProgramState, StakeState } from "@/types/stake";

/**
 * Default salt (all zeros)
 */
const DEFAULT_SALT = new Uint8Array([0, 0, 0, 0, 0, 0, 0, 0]);

/**
 * Initial state for a stake program vault
 */
const INITIAL_STAKE_PROGRAM_STATE: StakeProgramState = {
    stakingToken: undefined,
    depositToken: undefined,
    cooldown: undefined,
    vestingAmount: undefined,
    lastDistributionTime: undefined,
    totalAssets: undefined,
    minShares: undefined,
    vestingPeriod: undefined,
    rewarders: [],
    blacklist: [],
    stakingTokenSupply: undefined,
    isStakeStateLoaded: false,
    isError: false
};

/**
 * Stake store actions
 */
interface StakeActions {
    initialize: () => Promise<void>;
    getStakeState: (salt?: Uint8Array) => Promise<void>;
    vault: (salt?: Uint8Array) => StakeProgramState;
    reset: () => void;
}

export type StakeStore = StakeState & StakeActions;

/**
 * Initial state
 */
const INITIAL_STATE: StakeState = {
    vaults: {}
};

export function selectStakeVaultState(
    state: Pick<StakeStore, "vaults">,
    salt: Uint8Array = DEFAULT_SALT
): StakeProgramState {
    const saltNum = SolomonStake.saltToNumber(salt);

    if (saltNum in state.vaults) {
        return state.vaults[saltNum];
    }

    return INITIAL_STAKE_PROGRAM_STATE;
}

/**
 * Stake store for managing staking vault state
 * - Vault configuration (cooldown, vesting, tokens)
 * - Blacklist management
 * - Staking token supply
 *
 * Uses @solomon-labs/solomon-protocol SolomonStake for on-chain data fetching.
 */
export const useStakeStore = create<StakeStore>((set, get) => ({
    ...INITIAL_STATE,

    /**
     * Initialize the store
     */
    initialize: async (): Promise<void> => {
        // Optionally pre-fetch default vault state
        await get().getStakeState(DEFAULT_SALT);
    },

    /**
     * Reset store to initial state
     */
    reset: (): void => {
        set(INITIAL_STATE);
    },

    /**
     * Get stake program state for a vault by salt
     */
    vault: (salt: Uint8Array = DEFAULT_SALT): StakeProgramState => {
        return selectStakeVaultState(get(), salt);
    },

    /**
     * Fetch stake state from on-chain program using SolomonStake
     */
    getStakeState: async (salt: Uint8Array = DEFAULT_SALT): Promise<void> => {
        const saltNum = SolomonStake.saltToNumber(salt);

        // Set loading state
        set((state) => ({
            vaults: {
                ...state.vaults,
                [saltNum]: {
                    ...(state.vaults[saltNum] ?? INITIAL_STAKE_PROGRAM_STATE),
                    isStakeStateLoaded: false,
                    isError: false
                }
            }
        }));

        try {
            // Fetch vault state using SolomonStake
            const vaultState = await SolomonStake.fetchVaultState(salt);

            // Get staking token PDA
            const [stakingTokenPDA] = await SolomonStake.getStakingTokenPDA(vaultState.address);

            // Fetch staking token supply from chain
            const stakingTokenMint = await ConnectionManager.getMintInfo(stakingTokenPDA);
            const stakingTokenSupply = stakingTokenMint ? new BigDecimal(stakingTokenMint.supply) : undefined;

            set((state) => ({
                vaults: {
                    ...state.vaults,
                    [saltNum]: {
                        stakingToken: stakingTokenPDA as PublicKey,
                        depositToken: vaultState.depositToken as PublicKey,
                        cooldown: vaultState.cooldown,
                        vestingAmount: new BigDecimal(vaultState.vestingAmount),
                        lastDistributionTime: vaultState.lastDistributionTime,
                        totalAssets: new BigDecimal(vaultState.totalAssets),
                        minShares: new BigDecimal(vaultState.minShares),
                        vestingPeriod: vaultState.vestingPeriod,
                        rewarders: vaultState.rewarders as PublicKey[],
                        blacklist: [],
                        stakingTokenSupply,
                        isStakeStateLoaded: true,
                        isError: false
                    }
                }
            }));
        } catch (error) {
            console.error("Error fetching stake state:", error);
            set((state) => ({
                vaults: {
                    ...state.vaults,
                    [saltNum]: {
                        ...(state.vaults[saltNum] ?? INITIAL_STAKE_PROGRAM_STATE),
                        isStakeStateLoaded: true,
                        isError: true
                    }
                }
            }));
        }
    }
}));

export function useStakeVaultState(salt: Uint8Array = DEFAULT_SALT): StakeProgramState {
    const saltNum = SolomonStake.saltToNumber(salt);

    return useStakeStore((state) => {
        if (saltNum in state.vaults) {
            return state.vaults[saltNum];
        }

        return INITIAL_STAKE_PROGRAM_STATE;
    });
}
